import ReactGA from 'react-ga';

const ATTRIBUTION_KEY = 'gogo_funnel_attribution';

export type FunnelAttribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrerUrl?: string;
};

export type FunnelEventName =
  | 'org_created'
  | 'subscription_page_viewed'
  | 'org_guard_redirect'
  | 'marketing_landed'
  | 'marketing_signup_click'
  | 'empty_state_cta_click'
  | 'wix_connect_started'
  | 'wix_connect_completed'
  | 'wix_start_trial_clicked';

type AppInsightsLike = {
  trackEvent: (telemetry: {
    name: string;
    properties?: Record<string, unknown>;
  }) => void;
};

declare global {
  interface Window {
    appInsights?: AppInsightsLike;
  }
}

function isNonProduction(): boolean {
  return process.env.NODE_ENV !== 'production';
}

/** Capture UTM params + document referrer into sessionStorage (first load only). */
export function captureAttributionFromUrl(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): FunnelAttribution {
  if (typeof window === 'undefined') {
    return {};
  }

  const existing = getAttribution();
  if (
    existing.utmSource ||
    existing.utmMedium ||
    existing.utmCampaign ||
    existing.referrerUrl
  ) {
    return existing;
  }

  const params = new URLSearchParams(search);
  const attribution: FunnelAttribution = {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    referrerUrl: document.referrer || undefined,
  };

  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // ignore storage failures
  }

  if (
    attribution.utmSource ||
    attribution.utmMedium ||
    attribution.utmCampaign ||
    attribution.referrerUrl
  ) {
    trackFunnelEvent('marketing_landed', { ...attribution });
  }

  return attribution;
}

export function getAttribution(): FunnelAttribution {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as FunnelAttribution;
  } catch {
    return {};
  }
}

/** Fire a funnel event to ReactGA + Application Insights (when available). */
export function trackFunnelEvent(
  name: FunnelEventName | string,
  properties: Record<string, unknown> = {},
): void {
  const attribution = getAttribution();
  const payload = {
    ...attribution,
    ...properties,
  };

  if (isNonProduction()) {
    console.debug('[funnel]', name, payload);
  }

  try {
    const reason = properties.reason;
    ReactGA.event({
      category: 'funnel',
      action: name,
      label: typeof reason === 'string' ? reason : undefined,
    });
  } catch {
    // ReactGA may be uninitialized in some environments
  }

  try {
    window.appInsights?.trackEvent({
      name,
      properties: payload,
    });
  } catch {
    // ignore App Insights failures
  }
}
